import { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import CustomTabs from "../../components/CustomTabs";
import LogsComments from "../../components/LogsComments";
import Overlay from "../../components/overlay";
import { fuelGeneralDetails, getFuelLogDetails } from "../../store/Slices/FuelSlice";
import { formatDate } from "../../utils/helper";
import { images, imagesAlt } from "../../utils/images";
import SpinnerLoader from "../../utils/SpinnerLoader";
import { activeDetailsIcon, detailsIcon, dollarActiveIcon, dollarIcon, mapViewActiveIcon, mapViewIcon, trackerActiveIcon, trackerIcon, violationActiveIcon, violationIcon } from "../../utils/svgs";
import "../../styles/Fuel.css";
import FuelFinance from "./FuelFinance";
import FuelMap from "./FuelMap";
import FuelTimeline from "./FuelTimeline";
import FuelViolations from "./FuelViolations";

const FuelDetails = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { id } = useParams();

    const [activeTab, setActiveTab] = useState("detail");
    const [activeTripTimelineView, setActiveTripTimelineView] = useState("map");

    const { getFuelLogDetailsData, fuelGeneralDetailsData,
        fuelGeneralDetailsLoading, getFuelLogDetailsLoading
    } = useSelector((state) => state.fuel)

    const totalFuelCost = fuelGeneralDetailsData?.reduce((total, fuel) => total + parseInt(fuel?.total_fuel_cost || 0), 0);
    const totalClaimedFuel = fuelGeneralDetailsData?.reduce((total, fuel) => total + parseInt(fuel?.claimed_fueled || 0), 0);

    const fuelTrendData = fuelGeneralDetailsData.map(item => ({
        x: new Date(item.refuel_at).getTime(),
        y: Number(item.claimed_fueled),
    }));

    useEffect(() => {
        if (id) {
            dispatch(fuelGeneralDetails(id));
            dispatch(getFuelLogDetails(id));
        }
    }, [])

    const tripsTabs = [
        {
            key: "detail",
            label: "General Details",
            icon: activeTab === "detail" ? activeDetailsIcon : detailsIcon,
        },
        {
            key: "finance",
            label: "Finance",
            icon: activeTab === "finance" ? dollarActiveIcon : dollarIcon,
        },
        {
            key: "violation",
            label: "Violations",
            icon: activeTab === "violation" ? violationActiveIcon : violationIcon,
        }
    ];

    if (fuelGeneralDetailsLoading || getFuelLogDetailsLoading) {
        return <SpinnerLoader />
    }

    return (
        <div>
            <Breadcrumbs values={["Fuel", "Fuel Logs Details"]} />

            <div className="page_header">
                <h2>Fuel Logs Details</h2>

                <div className="d-flex align-items-center gap-2">
                    <Button
                        cancelBtn={() => {
                            navigate(-1);
                        }}
                        type="button"
                        shouldRow={false}
                        isShowSubmitBtn={false}
                    />
                </div>
            </div>

            <div className="page_container">
                <Row>
                    <Col md={9}>
                        <div className="trip_details_main_div">
                            <div>
                                <h6> ID: <span>{getFuelLogDetailsData?.trip_id || "N/A"}</span></h6>
                            </div>
                            <CustomTabs
                                tabs={tripsTabs}
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                            />

                            {
                                activeTab === "detail" &&
                                <Row>
                                    <Col md={12}>
                                        <div className="content_box">
                                            <h6>Transactional Overview</h6>
                                            <p>Fuel consumption and refill patterns across trip duration.</p>

                                            <div className="transactional_overview">
                                                <Row>
                                                    <Col md={3}>Vehicle ID</Col>
                                                    <Col md={9}>{getFuelLogDetailsData?.vehicle?.registration_number || "N/A"}</Col>

                                                    <Col md={3}>Driver Name</Col>
                                                    <Col md={9}>{getFuelLogDetailsData?.primary_driver?.username || "N/A"}</Col>

                                                    <Col md={3}>Reported By</Col>
                                                    <Col md={9}>{getFuelLogDetailsData?.reported_by || "N/A"}</Col>

                                                    <Col md={3}>Refuel Log</Col>
                                                    <Col md={9}>
                                                        <ul>
                                                            {
                                                                fuelGeneralDetailsData?.map((fuel) => (
                                                                    <li>{formatDate(fuel?.refuel_at)}</li>
                                                                ))
                                                            }
                                                        </ul>
                                                    </Col>
                                                    <Col md={3}>Filling Station</Col>
                                                    <Col md={9}>
                                                        <ul>
                                                            {
                                                                fuelGeneralDetailsData?.map((fuel) => (
                                                                    <li>{fuel?.fuel_station?.name + ", " + fuel?.fuel_station?.location}</li>
                                                                ))
                                                            }
                                                        </ul>
                                                    </Col>

                                                    <Col md={3}>Odometer Reading</Col>
                                                    <Col md={9}>{getFuelLogDetailsData?.vehicle?.odm || "N/A"}</Col>

                                                    <Col md={3}>Liters Filled (Sensor)</Col>
                                                    <Col md={9}>{totalClaimedFuel} Liters</Col>

                                                    <Col md={3}>Refill Cost</Col>
                                                    <Col md={9}>{totalFuelCost} PKR</Col>
                                                </Row>
                                            </div>
                                        </div>
                                        <div className="content_box">
                                            <h6>Trip Refuelling Timeline</h6>

                                            <FuelTimeline fuelGeneralDetailsData={fuelGeneralDetailsData}
                                                totalFuelCost={totalFuelCost}
                                                totalClaimedFuel={totalClaimedFuel}
                                            />
                                        </div>
                                        <div className="content_box">
                                            <div className="d-flex justify-content-between">
                                                <div>
                                                    <h6>Trip Checkpoint Timeline</h6>
                                                    <p>Information of Trip Checkpoint Timeline</p>
                                                </div>
                                                <div className="d-flex align-items-start">
                                                    <button type="button" className={activeTripTimelineView === "map" ? "map_active" : "map"} onClick={() => setActiveTripTimelineView("map")}>
                                                        {activeTripTimelineView === "map" ? mapViewActiveIcon : mapViewIcon} Map View
                                                    </button>
                                                    <button type="button" className={activeTripTimelineView === "tracker" ? "tracker_active" : "tracker"} onClick={() => setActiveTripTimelineView("tracker")}>
                                                        {activeTripTimelineView === "tracker" ? trackerActiveIcon : trackerIcon} Trend View
                                                    </button>
                                                </div>
                                            </div>

                                            {activeTripTimelineView === "tracker" &&

                                                <div>
                                                    <ReactApexChart
                                                        type="line"
                                                        height={320}
                                                        series={[
                                                            {
                                                                name: "Fuel Filled",
                                                                data: fuelTrendData,
                                                            },
                                                        ]}
                                                        options={{
                                                            chart: {
                                                                type: "line",
                                                                zoom: { enabled: false },
                                                                toolbar: { show: false },
                                                            },
                                                            stroke: { curve: "smooth", width: 2.8 },
                                                            markers: {
                                                                size: 6,
                                                                strokeWidth: 2,
                                                                hover: { size: 8 },
                                                            },
                                                            colors: ["#F59E0B"],
                                                            xaxis: {
                                                                type: "datetime",
                                                                title: {
                                                                    text: "Refuel Date",
                                                                },
                                                                labels: {
                                                                    format: "dd MMM HH:mm",
                                                                },
                                                            },
                                                            yaxis: {
                                                                title: {
                                                                    text: "Fuel Filled (Liters)",
                                                                },
                                                                labels: {
                                                                    formatter: (val) => `${val} L`,
                                                                },
                                                            },
                                                            tooltip: {
                                                                shared: false,
                                                                intersect: true,
                                                                x: {
                                                                    format: "dd MMM yyyy HH:mm",
                                                                },
                                                                y: {
                                                                    formatter: (val) => `${val} Liters`,
                                                                },
                                                            },
                                                        }}
                                                    />
                                                </div>

                                            }
                                            {activeTripTimelineView === "map" &&
                                                <div style={{ width: "100%", height: "400px", marginTop: "12px" }}>
                                                    <FuelMap />
                                                    {/* <TripMap detailView={true} /> */}
                                                </div>
                                            }
                                        </div>

                                        <div style={{ position: "relative" }}>
                                            <Overlay />

                                            <div className="content_box">
                                                <h6>Refueling Visual Evidence</h6>
                                                <p>Dashcam Image View of fuelling vehicle</p>

                                                <Row>
                                                    <Col md={6}>
                                                        <img src={images.refuelingSensorImg1} alt={imagesAlt.refuelingSensorImg1} />
                                                    </Col>
                                                    <Col md={6}>
                                                        <img src={images.refuelingSensorImg2} alt={imagesAlt.refuelingSensorImg2} />
                                                    </Col>
                                                </Row>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            }
                            {
                                activeTab === "finance" && <FuelFinance />
                            }
                            {
                                activeTab === "violation" && <FuelViolations />
                            }
                        </div>
                    </Col>
                    <Col md={3}>
                        <div className="fuel_log_details_sidebar">
                            <LogsComments />
                        </div>
                    </Col>
                </Row>
            </div>
        </div>
    )
}

export default FuelDetails
